import * as React from 'react';

import * as PopoverPrimitive from '@radix-ui/react-popover';
import { type VariantProps, tv } from 'tailwind-variants';

import { cn } from 'common/style/utils';

const popoverContentVariants = tv({
  slots: {
    base: [
      'outline-none box-border bg-popover rounded-sm p-4 shadow-md z-[3000]',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
      'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
      'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      'origin-(--radix-popover-content-transform-origin)',
    ],
  },
  variants: {
    fitTrigger: {
      true: {
        base: 'min-w-[var(--radix-popover-trigger-width)]',
      },
    },
    bordered: {
      true: {
        base: 'base-border',
      },
    },
  },
  defaultVariants: {
    bordered: true,
    fitTrigger: true,
  },
});

function Popover({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root {...props} />;
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger {...props} />;
}

export type PopoverContentProps = React.ComponentProps<typeof PopoverPrimitive.Content> &
  VariantProps<typeof popoverContentVariants>;

function PopoverContent({
  className,
  side = 'bottom',
  align = 'start',
  sideOffset = 4,
  fitTrigger,
  bordered,
  ...props
}: PopoverContentProps) {
  const styles = popoverContentVariants({ bordered, fitTrigger });

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        side={side}
        sideOffset={sideOffset}
        className={cn(styles.base({ className }))}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor {...props} />;
}

function PopoverClose({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Close>) {
  return <PopoverPrimitive.Close {...props} />;
}

Popover.Anchor = PopoverAnchor;
Popover.Trigger = PopoverTrigger;
Popover.Content = PopoverContent;
Popover.CloseButton = PopoverClose;

export default Popover;
